import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PopupProps {
  map: google.maps.Map;
  position: google.maps.LatLngLiteral;
  children: any;
  visible: boolean;
}

const CustomPopup: React.FC<PopupProps> = ({ map, position, children, visible }) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const overlayRef = useRef<google.maps.OverlayView | null>(null);

  useEffect(() => {
    if (!map || !overlayRef.current) return;

    const listener = google.maps.event.addListener(map, 'idle', () => {
      overlayRef.current?.draw();
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [map]);

  useEffect(() => {
    if (!map || !visible || !children) return;

    const containerDiv = document.createElement('div');
    containerDiv.classList.add('popup-container');

    class Popup extends google.maps.OverlayView {
      onAdd() {
        this.getPanes()?.floatPane.appendChild(containerDiv);
        // 🔥 Force redraw AFTER browser layout + map settle
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.draw();
          });
        });
      }

      onRemove() {
        containerDiv.parentElement?.removeChild(containerDiv);
      }

      draw() {
        const projection = this.getProjection();
        if (!projection) return;

        const divPosition = projection.fromLatLngToDivPixel(new google.maps.LatLng(position));
        if (!divPosition) return;

        containerDiv.style.left = `${divPosition.x + 25}px`;
        const height = containerDiv.getBoundingClientRect().height || 0;
        containerDiv.style.top = `${divPosition.y - height + 26}px`;
        containerDiv.style.position = 'absolute';
        containerDiv.style.opacity = '.8';
        containerDiv.style.display = visible ? '' : 'none';
      }
    }

    Popup.preventMapHitsAndGesturesFrom(containerDiv);

    // console.log(containerDiv, "cotainerdiv")
    const popup = new Popup();
    popup.setMap(map);
    overlayRef.current = popup;
    setContainer(containerDiv);

    return () => {
      popup.setMap(null);
    }; // Cleanup
  }, [visible, position]);

  return container && visible && children && createPortal(children, container);
};

export default CustomPopup;
